import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
	ClassicEditor,
	Alignment,
	AutoImage,
	Autosave,
	Base64UploadAdapter,
	BlockQuote,
	Bold,
	Emoji,
	Essentials,
	FontBackgroundColor,
	FontColor,
	FontFamily,
	FontSize,
	Heading,
	HorizontalLine,
	ImageBlock,
	ImageCaption,
	ImageInline,
	ImageInsert,
	ImageInsertViaUrl,
	ImageResize,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	Indent,
	IndentBlock,
	Italic,
	Link,
	LinkImage,
	List,
	ListProperties,
	MediaEmbed,
	Mention,
	Paragraph,
	PasteFromOffice,
	Strikethrough,
	Table,
	TableCaption,
	TableCellProperties,
	TableColumnResize,
	TableProperties,
	TableToolbar,
	TodoList,
	Underline,
	WordCount
} from 'ckeditor5';

import 'ckeditor5/ckeditor5.css';
import '../styles/ckeditor5.css'; // Custom dark theme styling
import { cleanPastedContent } from '../common/v5/utils.js';
import '../styles/ckeditor5-avatar.css'; // Avatar dialog styling
import AvatarPlugin from './ckeditor5-plugins/AvatarPlugin';
import '../styles/fileArchivePlugin.css'; // File archive plugin styles

// Import custom file archive plugin
import FileArchive from './FileArchivePlugin';

// Import custom upload adapter
import CentionUploadAdapter from './ckeditor5-plugins/CentionUploadAdapter';

// Import custom image marker plugin
import MarkUploadedImages from './ckeditor5-plugins/MarkUploadedImages';

// Import clipboard image upload plugin
import ClipboardImageUpload from './ckeditor5-plugins/ClipboardImageUpload';

// Import non-image file drop upload plugin (drag & drop to attachment panel)
import FileDropUpload from './ckeditor5-plugins/FileDropUpload';

const LICENSE_KEY = 'GPL'; // or replace with your license key
const LEGACY_FONT_SIZE_POINTS = [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48];
const LEGACY_FONT_SIZE_OPTIONS = LEGACY_FONT_SIZE_POINTS.map((size) => ({
	title: `${size}pt`,
	model: `${size}pt`,
	view: {
		name: 'span',
		styles: {
			'font-size': `${size}pt`
		}
	},
	upcastAlso: [
		{
			styles: {
				'font-size': `${size}px`
			}
		}
	]
}));

const normalizeFontSizeToPt = (fontSize) => {
	if (fontSize === undefined || fontSize === null || fontSize === '') {
		return null;
	}

	const value = String(fontSize).trim();
	const match = value.match(/^(\d+(?:\.\d+)?)(?:px|pt)?$/i);

	return match ? `${match[1]}pt` : value;
};

const CKEditor5Component = ({
	// Core data and content props
	data = '',
	defaultContent = '',
	initialData = '',
	
	// Event handlers
	onChange = () => {},
	onReady = () => {},
	onError = () => {},
	onFocus = () => {},
	onBlur = () => {},
	onKeyDown = () => {},
	onSelectText = () => {},
	onResetInsertionText = () => {},
	onEditorReady = () => {},
	
	// CKEditor 4 specific event handlers
	onKeydown = () => {}, // Alternative casing used in CK4
	onDragnDropFiles = () => {},
	onFileDrop = () => {},
	onLibraryClick = () => {},
	onRecordEditorSize = () => {},
	onUndoImage = () => {},
	onPaste = () => {},
	onAnswerChange = () => {},
	onSetData = () => {},
	onAppendAnswer = () => {},
	onToggleKnowledgeBase = () => {},
	onResetContent = () => {},
	onSetContent = () => {},
	callbackParent = () => {},
	
	// Configuration and behavior props
	config = {},
	disabled = false,
	hide = false,
	hidden = false,
	control = false,
	block = false,
	startupFocus = false,
	isReadOnly = false,
	readOnlyOnUnmount = true,
	
	// Styling and appearance props
	height = '200px',
	placeholder = '',
	placeHolderText = '', // CK4 uses this naming
	className = '',
	myClass = '',
	style = {},
	bgColor = '',
	txtColor = '',
	
	// Toolbar and UI props
	hideToolbar = false,
	toolbarID = '',
	simpleToolbar = false,
	simpleEditor = false,
	twoRowToolbar = false,
	hideSpellchecker = false,
	defShowSpellchk = true,
	
	// Font and text props
	defaultFontFamily,
	defaultFontSize,
	spellLanguages = [],
	
	// Resize and layout props
	showResize = false,
	resizeMinHeight = 50,
	resizeMaxHeight,
	recordSize = false,
	autoGrow = false,
	autoGrowMinHeight,
	autoGrowMaxHeight,
	setHeight,
	verticalView = false,
	editorMaxHeight,
	
	// Feature flags and advanced props
	showWordCount = false,
	insertionText = '',
	id = '',
	syncKey,
	enableFullScreen = false,
	tableTools = false,
	defShowYoutube = false,
	showBase64Image = false,
	avatarTemplate = false,
	wrapImageInContainer = false,
	
	// File and media props
	fileArchiveImages = [],
	showFileArchive,
	imageResize,
	imageMaxSize,
	
	// Upload configuration
	useServerUpload = true,
	uploadUrl = '',
	onImageUpload = () => {},
	
		// Library and template props
	showLibrary = false,
	libraryId,

	// Agent data for avatar functionality
	agent,

	// Suggestion and UI behavior props
	suggestBoxScrollBar,
	
	// Chat-specific props
	isChat = false,
	simpleChatToobar = false,
	agentGroupChatToolbar = false,
	resize_dir = 'vertical',
	showReplyPanel = true, // Add the showReplyPanel prop
	
	// Signature editor flag for enhanced email compatibility
	isSignatureEditor = false,
	
	// QA and testing props
	'data-qa-id': dataQaId = '',
	name = '' // Form field name prop
}) => {
	const editorContainerRef = useRef(null);
	const editorRef = useRef(null);
	const editorWordCountRef = useRef(null);
	const heightObserverRef = useRef(null);
	const userResizedHeightRef = useRef(null);
	const lastHeightPropRef = useRef(height);
	const [isLayoutReady, setIsLayoutReady] = useState(false);
	const [isDarkMode, setIsDarkMode] = useState(false);
	const prevInsertionTextRef = useRef('');
	const isInternalChangeRef = useRef(false);
	const lastExternalDataRef = useRef('');
	
	// Use defaultContent as fallback if data is empty, similar to how CKEditor4 works
	const editorData = data || defaultContent || initialData || '';
	
	// Store initial data and only use it once for the data prop
	// This prevents the editor from re-initializing on every parent state update
	const [initialEditorData] = useState(editorData);
	
	// Handle placeholder text (CKEditor4 uses placeHolderText)
	const finalPlaceholder = placeholder || placeHolderText || '';
	
	// Handle class names (CKEditor4 uses myClass)
	const finalClassName = className || myClass || '';

	const applyLegacyEditorColors = useCallback((editor = editorRef.current) => {
		if (!editor) {
			return;
		}

		const editableElement = editor.ui.getEditableElement();
		const editorContainer = editableElement?.closest('.ckeditor5-component');
		const textColor = txtColor || (bgColor ? 'black' : '');

		if (editorContainer) {
			if (bgColor) {
				editorContainer.style.setProperty('--ck-color-base-background', bgColor);
				editorContainer.style.setProperty('--ck-color-widget-editable-focus-background', bgColor);
				editorContainer.style.setProperty('--ck-legacy-editor-background', bgColor);
				editorContainer.style.backgroundColor = bgColor;
			} else {
				editorContainer.style.removeProperty('--ck-color-base-background');
				editorContainer.style.removeProperty('--ck-color-widget-editable-focus-background');
				editorContainer.style.removeProperty('--ck-legacy-editor-background');
				editorContainer.style.backgroundColor = '';
			}

			if (textColor) {
				editorContainer.style.setProperty('--ck-color-text', textColor, 'important');
			} else {
				editorContainer.style.removeProperty('--ck-color-text');
			}
		}

		if (editableElement) {
			if (bgColor) {
				editableElement.style.setProperty('background-color', bgColor, 'important');
			} else {
				editableElement.style.removeProperty('background-color');
			}

			if (textColor) {
				editableElement.style.setProperty('color', textColor, 'important');
			} else {
				editableElement.style.removeProperty('color');
			}
		}
	}, [bgColor, txtColor]);

	// Determine whether to use server upload or base64
	// Default is true (server upload) to match CKEditor 4 behavior
	// Can be set to false for contexts where clients can't access authenticated server URLs:
	// - Chat: clients in chat widget can't access /errand/uploadAnswerAttachment URLs
	// - Collaboration: external experts need base64 images in emails
	const finalUseServerUpload = useServerUpload !== false; // Only false if explicitly set to false

	// Text selection detection function similar to CKEditor4's detectSelection
	const detectSelection = useCallback((editor) => {
		try {
			if (!editor || !onSelectText) {
				return;
			}

			const selection = editor.model.document.selection;
			const viewSelection = editor.editing.view.document.selection;
			
			// Get selected text
			let selectedText = '';
			if (!selection.isCollapsed) {
				for (const range of selection.getRanges()) {
					for (const item of range.getItems()) {
						if (item.is('$text') || item.is('$textProxy')) {
							selectedText += item.data;
						}
					}
				}
			}

			// Clean up the selected text (remove newlines like CKEditor4 does)
			const cleanedSelection = selectedText.replace(/[\n\r]/g, '');
			
			if (cleanedSelection.length > 0 && !selection.isCollapsed) {
				// Calculate position using view selection
				try {
					const viewRange = editor.editing.mapper.toViewRange(selection.getFirstRange());
					const domRange = editor.editing.view.domConverter.viewRangeToDom(viewRange);
					
					if (domRange) {
						// Get the selection rectangle
						const rect = domRange.getBoundingClientRect();
						
						// Get the editor's editable element for relative positioning
						const editableElement = editor.editing.view.document.selection.editableElement;
						const editableDomElement = editor.editing.view.domConverter.viewToDom(editableElement);
						const editableRect = editableDomElement.getBoundingClientRect();
						
						// Calculate position relative to the editor container
						const position = {
							top: rect.top - editableRect.top,
							left: rect.left - editableRect.left
						};
						
						onSelectText(selectedText, position, false);
					} else {
						// Invalid range
						onSelectText('', '', true);
					}
				} catch (error) {
					console.warn('Error calculating selection position:', error);
					onSelectText('', '', true);
				}
			} else {
				// No valid selection
				onSelectText('', '', false);
			}
		} catch (error) {
			console.error('Error in detectSelection:', error);
			onSelectText('', '', true);
		}
	}, [onSelectText]);

	useEffect(() => {
		setIsLayoutReady(true);
		
		// Check dark mode on component mount
		const checkDarkMode = () => {
			const theme = localStorage.getItem('theme');
			const isDark = theme === "true";
			setIsDarkMode(isDark);
			
			// Apply theme class to body for global emoji picker styling
			if (isDark) {
				document.body.classList.add('dark-theme');
			} else {
				document.body.classList.remove('dark-theme');
			}
		};
		
		checkDarkMode();
		
		// Listen for theme changes
		const handleStorageChange = (e) => {
			if (e.key === 'theme') {
				checkDarkMode();
			}
		};
		
		window.addEventListener('storage', handleStorageChange);
		
		return () => {
			setIsLayoutReady(false);
			window.removeEventListener('storage', handleStorageChange);
			// Clean up body class when component unmounts
			document.body.classList.remove('dark-theme');
		};
	}, []);

	// Handle external data changes (prevent cursor reset on internal changes)
	useEffect(() => {
		if (editorRef.current && editorData !== lastExternalDataRef.current) {
			const currentData = editorRef.current.getData();
			// Only setData if the content is actually different
			// This handles external updates like loading new data, undo/redo from parent, etc.
			if (currentData !== editorData) {
				const isClearing = editorData === '' && currentData !== '';
				
				if (isClearing || !isInternalChangeRef.current) {
					console.log('CKEditor5: External data change detected', {
						isClearing,
						isInternalChange: isInternalChangeRef.current,
						currentDataLength: currentData.length,
						newDataLength: editorData.length
					});
					
					// Save cursor position before setData (but not for clearing operations)
					const selection = editorRef.current.model.document.selection;
					const range = selection.getFirstRange();
					let savedPosition = null;
					
					if (!isClearing) {
						try {
							// Try to save the cursor position
							if (range) {
								savedPosition = {
									path: range.start.path.slice(),
									offset: range.start.offset
								};
							}
						} catch (e) {
							console.warn('Could not save cursor position:', e);
						}
					}
					
					// Set the new data
					editorRef.current.setData(editorData);
					
					// Try to restore cursor position after setData
					if (savedPosition && !isClearing) {
						setTimeout(() => {
							try {
								editorRef.current.model.change(writer => {
									const root = editorRef.current.model.document.getRoot();
									try {
										const position = writer.createPositionFromPath(root, savedPosition.path, savedPosition.offset);
										writer.setSelection(position);
									} catch (e) {
										// If we can't restore to exact position, place at end
										const endPosition = writer.createPositionAt(root, 'end');
										writer.setSelection(endPosition);
									}
								});
							} catch (e) {
								console.warn('Could not restore cursor position:', e);
							}
						}, 0);
					}
				}
			}
			// Reset the internal change flag
			isInternalChangeRef.current = false;
			lastExternalDataRef.current = editorData;
		}
	}, [editorData]);

	const prevSyncKeyRef = useRef(syncKey);
	useEffect(() => {
		if (prevSyncKeyRef.current === syncKey) {
			return;
		}
		prevSyncKeyRef.current = syncKey;
		if (!editorRef.current) {
			lastExternalDataRef.current = editorData;
			return;
		}
		const currentData = editorRef.current.getData();
		if (currentData !== editorData) {
			editorRef.current.setData(editorData);
		}
		isInternalChangeRef.current = false;
		lastExternalDataRef.current = editorData;
	}, [syncKey]);

	// Handle insertionText changes (for knowledge base insertion)
	useEffect(() => {
		if (insertionText && 
			insertionText !== prevInsertionTextRef.current && 
			!prevInsertionTextRef.current && 
			editorRef.current) {
			
			// Insert the content at current cursor position
			editorRef.current.model.change(writer => {
				const selection = editorRef.current.model.document.selection;
				const position = selection.getFirstPosition();
				
				// Create document fragment from HTML
				const viewFragment = editorRef.current.data.processor.toView(insertionText);
				const modelFragment = editorRef.current.data.toModel(viewFragment);
				
				// Insert the content
				editorRef.current.model.insertContent(modelFragment, position);
			});
			
			// Reset the insertion text
			onResetInsertionText();
		}
		
		// Update the previous insertion text reference
		prevInsertionTextRef.current = insertionText;
	}, [insertionText, onResetInsertionText]);

	// Process image dimensions for email compatibility
	const processImageDimensionsForEmail = useCallback((htmlContent) => {
		if (!htmlContent) return htmlContent;
		
		// Find all img tags and ensure they have both width/height attributes and CSS styles
		return htmlContent.replace(/<img([^>]*?)>/gi, (match, attributes) => {
			let processedAttributes = attributes;
			
			// Extract style attribute
			const styleMatch = attributes.match(/\bstyle\s*=\s*["']([^"']*)["']/i);
			let existingStyle = styleMatch ? styleMatch[1] : '';
			
			// Extract width and height from CSS if present
			const cssWidthMatch = existingStyle.match(/width\s*:\s*(\d+(?:\.\d+)?)px/i);
			const cssHeightMatch = existingStyle.match(/height\s*:\s*(\d+(?:\.\d+)?)px/i);
			
			// Extract existing HTML width/height attributes
			const htmlWidthMatch = attributes.match(/\bwidth\s*=\s*["']?(\d+(?:\.\d+)?)["']?/i);
			const htmlHeightMatch = attributes.match(/\bheight\s*=\s*["']?(\d+(?:\.\d+)?)["']?/i);
			
			let needsUpdate = false;
			let newStyle = existingStyle;
			
			// If we have CSS width but no HTML width attribute, add it
			if (cssWidthMatch && !htmlWidthMatch) {
				const width = Math.round(parseFloat(cssWidthMatch[1]));
				processedAttributes += ` width="${width}"`;
				needsUpdate = true;
			}
			
			// If we have CSS height but no HTML height attribute, add it
			if (cssHeightMatch && !htmlHeightMatch) {
				const height = Math.round(parseFloat(cssHeightMatch[1]));
				processedAttributes += ` height="${height}"`;
				needsUpdate = true;
			}
			
			// If we have HTML width but no CSS width, add it to style
			if (htmlWidthMatch && !cssWidthMatch) {
				const width = htmlWidthMatch[1];
				newStyle += newStyle ? `; width: ${width}px` : `width: ${width}px`;
				needsUpdate = true;
			}
			
			// If we have HTML height but no CSS height, add it to style
			if (htmlHeightMatch && !cssHeightMatch) {
				const height = htmlHeightMatch[1];
				newStyle += newStyle ? `; height: ${height}px` : `height: ${height}px`;
				needsUpdate = true;
			}
			
			// Update style attribute if we modified it
			if (needsUpdate && newStyle !== existingStyle) {
				if (styleMatch) {
					// Replace existing style
					processedAttributes = processedAttributes.replace(/\bstyle\s*=\s*["'][^"']*["']/i, `style="${newStyle}"`);
				} else {
					// Add new style attribute
					processedAttributes += ` style="${newStyle}"`;
				}
			}
			
			return `<img${processedAttributes}>`;
		});
	}, []);

	// Handle toolbar visibility changes without remounting the editor
	// Update fileArchiveImages in editor config when it changes
	useEffect(() => {
		if (editorRef.current) {
			editorRef.current.config._fileArchiveImages = fileArchiveImages;
			console.log('Updated editor fileArchiveImages config:', fileArchiveImages);
		}
	}, [fileArchiveImages]);

	useEffect(() => {
		applyLegacyEditorColors();
	}, [applyLegacyEditorColors]);

	useEffect(() => {
		if (editorRef.current) {
			const toolbarElement = editorRef.current.ui.view.toolbar.element;
			if (toolbarElement) {
				// Hide toolbar if hideToolbar is true OR if the reply panel is collapsed (showReplyPanel is false)
				if (hideToolbar || !showReplyPanel) {
					toolbarElement.style.display = 'none';
				} else {
					toolbarElement.style.display = '';
				}
			}

		// Also update the editor height when showReplyPanel changes
		const editableElement = editorRef.current.ui.getEditableElement();
		if (editableElement && height) {
			// When panel collapses, clear saved user-resize so next expand starts at prop height
			if (!showReplyPanel) {
				userResizedHeightRef.current = null;
			}
			const actualHeight = showReplyPanel ? height : '30px'; // Use 30px to match DEFAULT_CKEDITOR_HEIGHT_CHAT
			editableElement.style.height = actualHeight;
			editableElement.style.minHeight = actualHeight;
			editableElement.style.resize = showReplyPanel ? 'vertical' : 'none'; // Disable resize when collapsed
				
				// When collapsed, also set max-height to prevent expansion
				if (!showReplyPanel) {
					editableElement.style.maxHeight = '30px';
					// Add important flag to make it harder to override
					editableElement.style.setProperty('height', '30px', 'important');
					editableElement.style.setProperty('min-height', '30px', 'important');
					editableElement.style.setProperty('max-height', '30px', 'important');
					editableElement.style.setProperty('resize', 'none', 'important');
					
					// Remove any resize handles that might exist
					const removeResizeHandles = () => {
						const container = editableElement.closest('.ckeditor5-component');
						if (container) {
							// Find and remove any resize handles
							const resizeHandles = container.querySelectorAll('[class*="resize"], [class*="handle"], .resize-handle, .resize-grip, .resize-corner');
							resizeHandles.forEach(handle => {
								handle.style.display = 'none';
								handle.style.visibility = 'hidden';
								handle.remove();
							});
							
							// Also check for resize handles in the editor itself
							const editorResizeHandles = editableElement.querySelectorAll('[class*="resize"], [class*="handle"]');
							editorResizeHandles.forEach(handle => {
								handle.style.display = 'none';
								handle.style.visibility = 'hidden';
								handle.remove();
							});
						}
					};
					
					// Remove resize handles immediately
					removeResizeHandles();
					
					// Also remove them after a short delay in case they're added later
					setTimeout(removeResizeHandles, 100);
					setTimeout(removeResizeHandles, 500);
					
					// Set up MutationObserver to enforce collapsed height
					if (heightObserverRef.current) {
						heightObserverRef.current.disconnect();
					}
					
					heightObserverRef.current = new MutationObserver((mutations) => {
						mutations.forEach((mutation) => {
							if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
								const target = mutation.target;
								if (target === editableElement && !showReplyPanel) {
									// Force the height back to 30px if something changed it
									const currentHeight = target.style.height;
									if (currentHeight !== '30px') {
										target.style.setProperty('height', '30px', 'important');
										target.style.setProperty('min-height', '30px', 'important');
										target.style.setProperty('max-height', '30px', 'important');
										target.style.setProperty('resize', 'none', 'important');
									}
								}
							}
							
							// Watch for any new resize handles being added
							if (mutation.type === 'childList' && !showReplyPanel) {
								mutation.addedNodes.forEach(node => {
									if (node.nodeType === Node.ELEMENT_NODE) {
										const element = node;
										if (element.classList && (
											element.className.includes('resize') || 
											element.className.includes('handle') ||
											element.tagName === 'RESIZE'
										)) {
											element.style.display = 'none';
											element.style.visibility = 'hidden';
											element.remove();
										}
									}
								});
							}
						});
					});
					
					heightObserverRef.current.observe(editableElement, {
						attributes: true,
						attributeFilter: ['style'],
						childList: true,
						subtree: true
					});
			} else {
				if (lastHeightPropRef.current !== height) {
					userResizedHeightRef.current = null;
					lastHeightPropRef.current = height;
				}
				const targetHeight = userResizedHeightRef.current || height;
				editableElement.style.setProperty('height', targetHeight, 'important');
				editableElement.style.setProperty('min-height', height, 'important');
				editableElement.style.setProperty('max-height', '600px', 'important');
				editableElement.style.setProperty('resize', 'vertical', 'important');
				
				// Disconnect observer when not collapsed
				if (heightObserverRef.current) {
					heightObserverRef.current.disconnect();
					heightObserverRef.current = null;
				}
			}
			}
		}
		
		// Cleanup observer on unmount or when dependencies change
		return () => {
			if (heightObserverRef.current) {
				heightObserverRef.current.disconnect();
				heightObserverRef.current = null;
			}
		};
	}, [hideToolbar, showReplyPanel, height]);

	const { editorConfig } = useMemo(() => {
		if (!isLayoutReady) {
			return {};
		}

	// Check if this is for ManualErrand
	const isManualErrand = toolbarID === 'ckev5meTop';
	
	// Determine if FileArchive should be shown
	// Priority: explicit showFileArchive prop, then check for specific toolbar/editor IDs
	// Show for: manual errand (ckev5meTop), regular errand reply (ckeditorTop/ckeditorv5), or explicitly enabled
	const shouldShowFileArchive = showFileArchive !== undefined 
		? showFileArchive 
		: (isManualErrand || 
			toolbarID === 'ckeditorTop' || 
			id === 'ckeditorv5' || 
			id === 'ckev5errand' || 
			id === 'ckev5me');
	
	// Configure toolbar based on toolbarID prop
	let toolbarConfig = {
		items: [
			'undo',
			'redo',
			'|',
			'heading',
			'|',
			'fontSize',
			'fontFamily',
			'fontColor',
			'fontBackgroundColor',
			'|',
			'numberedList',
			'bulletedList',
			'alignment',
			'|',
			'bold',
			'italic',
			'underline',
		'|',
		'emoji',
		...(isManualErrand ? [] : ['insertImage']), // insertImage before blockQuote if NOT manual errand
		...(shouldShowFileArchive && !isManualErrand ? ['FileArchive'] : []), // FileArchive before blockQuote if NOT manual errand
		'horizontalLine',
		'link',
		'mediaEmbed',
		'blockQuote',
		...(isManualErrand ? ['insertImage', 'FileArchive'] : []), // insertImage and FileArchive AFTER blockQuote for manual errand
		'|',
		'todoList',
		'outdent',
		'indent',
		'insertTable'
	],
	shouldNotGroupWhenFull: true // Allow grouping and multi-row by default
};

		// Handle different toolbar layouts based on toolbarID
		if (toolbarID === 'OneRow') {
			// For OneRow, use a more compact layout with grouping enabled
			toolbarConfig = {
				items: [
					'undo', 'redo', '|',
					'heading', '|',
					'fontSize', 'fontFamily', 'fontColor', 'fontBackgroundColor', '|',
					'bold', 'italic', 'underline', '|',
					'numberedList', 'bulletedList', 'alignment', '|',
					'emoji', 
					...(isManualErrand ? [] : ['insertImage']),
					...(shouldShowFileArchive && !isManualErrand ? ['FileArchive'] : []),
					'insertTable', 'horizontalLine', 'link', 'mediaEmbed', 'blockQuote',
					...(isManualErrand ? ['insertImage', 'FileArchive'] : []),
					'|',
					'todoList', 'outdent', 'indent'
				],
				shouldNotGroupWhenFull: true // Allow toolbar to wrap and group items
			};
		} else if (twoRowToolbar || toolbarID === 'TwoRow') {
			// Two-row toolbar layout
			toolbarConfig = {
				items: [
					'undo', 'redo', '|',
					'heading', '|',
					'fontSize', 'fontFamily', 'fontColor', 'fontBackgroundColor',
					'-', // Force line break
					'bold', 'italic', 'underline', 'strikethrough', '|',
					'numberedList', 'bulletedList', 'alignment', '|',
					'emoji',
					...(isManualErrand ? [] : ['insertImage']),
					...(shouldShowFileArchive && !isManualErrand ? ['FileArchive'] : []),
					'insertTable', 'horizontalLine', 'link', 'mediaEmbed', 'blockQuote',
					...(isManualErrand ? ['insertImage', 'FileArchive'] : []),
					'|',
					'todoList', 'outdent', 'indent'
				],
				shouldNotGroupWhenFull: false // Don't group when using explicit rows
			};
		} else if (simpleToolbar || simpleEditor) {
			// Simple toolbar with fewer options
			toolbarConfig = {
				items: [
					'undo', 'redo', '|',
					'bold', 'italic', 'underline', 'strikethrough', '|',
					'fontSize', 'fontColor', '|',
					'numberedList', 'bulletedList', '|',
					'insertTable', 'link', 'blockQuote'
				],
				shouldNotGroupWhenFull: true
			};
		}

		// Conditionally add insertAvatar to toolbar if avatarTemplate is enabled
		if (avatarTemplate) {
			// Find the position to insert the avatar button (after insertImage)
			const insertIndex = toolbarConfig.items.findIndex(item => item === 'insertImage');
			if (insertIndex !== -1) {
				toolbarConfig.items.splice(insertIndex + 1, 0, 'insertAvatar');
			} else {
				// If insertImage is not found, add after emoji
				const emojiIndex = toolbarConfig.items.findIndex(item => item === 'emoji');
				if (emojiIndex !== -1) {
					toolbarConfig.items.splice(emojiIndex + 1, 0, 'insertAvatar');
				}
			}
		}

		const basePlugins = [
			Alignment,
			AutoImage,
			Autosave,
			finalUseServerUpload ? CentionUploadAdapter : Base64UploadAdapter,
			BlockQuote,
			Bold,
			Emoji,
			Essentials,
			FileArchive, // Custom file archive plugin
			FontBackgroundColor,
			FontColor,
			FontFamily,
			FontSize,
			Heading,
			HorizontalLine,
			ImageBlock,
			ImageCaption,
			ImageInline,
			ImageInsert,
			ImageInsertViaUrl,
			ImageResize,
			ImageStyle,
			ImageToolbar,
			ImageUpload,
			Indent,
			IndentBlock,
			Italic,
			Link,
			LinkImage,
			List,
			ListProperties,
			MediaEmbed,
			Mention,
			Paragraph,
			PasteFromOffice,
			Strikethrough,
			Table,
			TableCaption,
			TableCellProperties,
			TableColumnResize,
			TableProperties,
			TableToolbar,
			TodoList,
			Underline,
			WordCount,
			FileDropUpload
		];

		// Add server upload plugins only when useServerUpload is enabled
		// These plugins are designed for server-side image handling and conflict with Base64UploadAdapter
		if (finalUseServerUpload) {
			basePlugins.push(ClipboardImageUpload); // Custom plugin to handle pasted images with server upload
			basePlugins.push(MarkUploadedImages); // Custom plugin to mark uploaded images
		}

		// Add AvatarPlugin if avatarTemplate is enabled
		if (avatarTemplate) {
			basePlugins.push(AvatarPlugin);
		}

		const defaultConfig = {
			toolbar: toolbarConfig,
			plugins: basePlugins,
			fontFamily: {
				options: [
					'default',
					'Arial, Helvetica, sans-serif',
					'Arial Black, sans-serif',
					'Averta, Verdana, sans-serif',
					'Calibri, sans-serif',
					'Comic Sans MS, cursive',
					'Courier New, Courier, monospace',
					'Georgia, serif',
					'Lucida Console, monospace',
					'Lucida Sans Unicode, Lucida Grande, sans-serif',
					'Montserrat, sans-serif',
					'Myriad Pro, Lucida Grande, sans-serif',
					'Tahoma, Geneva, sans-serif',
					'Times New Roman, Times, serif',
					'Trebuchet MS, Helvetica, sans-serif',
					'Verdana, Geneva, sans-serif'
				],
				supportAllValues: true
			},
			fontSize: {
				options: [
					'default',
					...LEGACY_FONT_SIZE_OPTIONS
				]
			},
			heading: {
				options: [
					{
						model: 'paragraph',
						title: 'Paragraph',
						class: 'ck-heading_paragraph'
					},
					{
						model: 'heading1',
						view: 'h1',
						title: 'Heading 1',
						class: 'ck-heading_heading1'
					},
					{
						model: 'heading2',
						view: 'h2',
						title: 'Heading 2',
						class: 'ck-heading_heading2'
					},
					{
						model: 'heading3',
						view: 'h3',
						title: 'Heading 3',
						class: 'ck-heading_heading3'
					},
					{
						model: 'heading4',
						view: 'h4',
						title: 'Heading 4',
						class: 'ck-heading_heading4'
					},
					{
						model: 'heading5',
						view: 'h5',
						title: 'Heading 5',
						class: 'ck-heading_heading5'
					},
					{
						model: 'heading6',
						view: 'h6',
						title: 'Heading 6',
						class: 'ck-heading_heading6'
					}
				]
			},
			list: {
				properties: {
					styles: true,
					startIndex: true,
					reversed: true
				}
			},
			image: {
				toolbar: [
					'toggleImageCaption',
					'|',
					'imageStyle:inline',
					'imageStyle:wrapText',
					'imageStyle:breakText',
					'|',
					'imageStyle:alignLeft',
					'imageStyle:alignCenter', 
					'imageStyle:alignRight',
					'|',
					'resizeImage'
				],
				// Configure resize for email compatibility (especially for signatures)
				resizeUnit: isSignatureEditor ? 'px' : '%', // Use pixels for signatures, percentage for others
				resizeOptions: isSignatureEditor ? [
					// Email-friendly resize options for signatures
					{
						name: 'resizeImage:original',
						value: null,
						icon: 'original'
					},
					{
						name: 'resizeImage:custom',
						value: 'custom',
						icon: 'custom'
					},
					{
						name: 'resizeImage:small',
						value: '25%',
						icon: 'small'
					},
					{
						name: 'resizeImage:medium',
						value: '50%',
						icon: 'medium'
					},
					{
						name: 'resizeImage:large',
						value: '75%',
						icon: 'large'
					}
				] : [
					// Standard resize options for other editors
					{
						name: 'resizeImage:original',
						value: null,
						icon: 'original'
					},
					{
						name: 'resizeImage:custom',
						value: 'custom',
						icon: 'custom'
					},
					{
						name: 'resizeImage:25',
						value: '25%',
						icon: 'small'
					},
					{
						name: 'resizeImage:50',
						value: '50%',
						icon: 'medium'
					},
					{
						name: 'resizeImage:75',
						value: '75%',
						icon: 'large'
					}
				]
			},
			table: {
				contentToolbar: [
					'tableColumn',
					'tableRow',
					'mergeTableCells',
					'tableProperties',
					'tableCellProperties',
					'toggleTableCaption'
				],
				styles: [
					'inline',
					'wrapText', 
					'breakText',
					'alignLeft',
					'alignCenter',
					'alignRight'
				]
			},
			licenseKey: LICENSE_KEY,
			link: {
				addTargetToExternalLinks: true,
				defaultProtocol: 'https://',
				decorators: {
					toggleDownloadable: {
						mode: 'manual',
						label: 'Downloadable',
						attributes: {
							download: 'file'
						}
					}
				}
			},
			mention: {
				feeds: [
					{
						marker: '@',
						feed: [
							/* See: https://ckeditor.com/docs/ckeditor5/latest/features/mentions.html */
						]
					}
				]
			},
			placeholder: finalPlaceholder,
			// Add avatar template configuration if avatarTemplate is enabled (matches CKEditor 4)
			...(avatarTemplate && {
				avatarTemplate: (process.env.CLOUDFRONT_URL || '') + '/img/cention-custom-avatar-template-image-should-not-use-for-another-purpose.svg'
			}),
			fileArchiveImages: fileArchiveImages, // Pass file archive images to the plugin
			wrapImageInContainer: wrapImageInContainer, // Pass image container setting
			centionFileDrop: {
				onDragnDropFiles: onDragnDropFiles,
				onFileDrop: onFileDrop
			},
			// Configure Cention Upload Adapter if server upload is enabled
			...(finalUseServerUpload && {
				centionUpload: {
					uploadUrl: uploadUrl || (typeof window !== 'undefined' && window.webserverRoot 
						? window.webserverRoot + 'errand/uploadAnswerAttachment' 
						: '/errand/uploadAnswerAttachment'),
					onUpload: onImageUpload
				}
			}),
			...config // Allow custom config to override defaults
		};

		return { editorConfig: defaultConfig };
	}, [isLayoutReady, config, finalPlaceholder, hideToolbar, toolbarID, twoRowToolbar, simpleToolbar, simpleEditor, isSignatureEditor, showReplyPanel, avatarTemplate, fileArchiveImages, wrapImageInContainer, uploadUrl, onImageUpload, onDragnDropFiles, onFileDrop, defaultFontSize]);

	const handleReady = (editor) => {
		editorRef.current = editor;
		
		lastExternalDataRef.current = initialEditorData;
		
		// Log image upload configuration for debugging
		console.log('CKEditor5: Editor ready', {
			editorId: id,
			useServerUpload: finalUseServerUpload,
			uploadUrl: uploadUrl || (window.webserverRoot + 'errand/uploadAnswerAttachment'),
			note: 'ALWAYS uploads to server (matching CKEditor 4 behavior)'
		});
		
		// Store initial fileArchiveImages in editor config
		editor.config._fileArchiveImages = fileArchiveImages;
		
		// Register editor in global registry for backward compatibility
		if (id) {
			if (!window.CKEditor5Instances) {
				window.CKEditor5Instances = {};
			}
			window.CKEditor5Instances[id] = editor;
			
			// Also provide CKEDITOR.instances compatibility for legacy code
			if (!window.CKEDITOR) {
				window.CKEDITOR = {};
			}
			if (!window.CKEDITOR.instances) {
				window.CKEDITOR.instances = {};
			}
			
			// Create a CKEditor 4-compatible wrapper for the editor instance
			window.CKEDITOR.instances[id] = {
				name: name || id, // Use name prop or fall back to id
				document: {
					getBody: () => ({
						getText: () => {
							const data = editor.getData();
							// Strip HTML tags to get plain text
							const tempDiv = document.createElement('div');
							tempDiv.innerHTML = data;
							return tempDiv.textContent || tempDiv.innerText || '';
						}
					})
				},
				getData: () => editor.getData(),
				setData: (data) => editor.setData(data),
				focus: () => editor.editing.view.focus(),
				setReadOnly: (isReadOnly) => {
					// CKEditor 5 uses enableReadOnlyMode/disableReadOnlyMode with feature ID
					const featureId = 'ckeditor4-compatibility';
					if (isReadOnly) {
						editor.enableReadOnlyMode(featureId);
					} else {
						editor.disableReadOnlyMode(featureId);
					}
				},
				// Add direct access to CKEditor 5 instance
				ck5Editor: editor
			};
		}
		
		if (height) {
			const editableElement = editor.ui.getEditableElement();
			if (editableElement) {
				const saveUserResize = () => {
					const rectHeight = Math.round(editableElement.getBoundingClientRect().height);
					const currentHeight = editableElement.style.height || (rectHeight ? rectHeight + 'px' : '');
					const currentHeightValue = parseInt(currentHeight, 10);
					const propHeightValue = parseInt(lastHeightPropRef.current, 10);
					if (currentHeight && currentHeight !== lastHeightPropRef.current && currentHeightValue >= propHeightValue) {
						userResizedHeightRef.current = currentHeight;
					}
				};
				editableElement.addEventListener('mouseup', saveUserResize);
				editableElement.addEventListener('pointerup', saveUserResize);
				window.addEventListener('mouseup', saveUserResize);
				window.addEventListener('pointerup', saveUserResize);

				const actualHeight = showReplyPanel ? height : '30px';

				if (!showReplyPanel) {
					editableElement.style.setProperty('height', '30px', 'important');
					editableElement.style.setProperty('min-height', '30px', 'important');
					editableElement.style.setProperty('max-height', '30px', 'important');
					editableElement.style.setProperty('resize', 'none', 'important');
				} else {
					editableElement.style.setProperty('height', actualHeight, 'important');
					editableElement.style.setProperty('min-height', actualHeight, 'important');
					editableElement.style.setProperty('max-height', '600px', 'important');
					editableElement.style.setProperty('resize', 'vertical', 'important');
				}
				editableElement.style.overflowY = 'auto';
			}
		}

		// Handle initial toolbar visibility
		const toolbarElement = editor.ui.view.toolbar.element;
		if (toolbarElement && (hideToolbar || !showReplyPanel)) {
			toolbarElement.style.display = 'none';
		}

		// Override CKEditor5's internal resize handling when collapsed
		if (!showReplyPanel) {
			// Find and disable any resize observers or handlers
			const editableElement = editor.ui.getEditableElement();
			if (editableElement) {
				// Override the style property setter to prevent height changes when collapsed
				const originalSetProperty = editableElement.style.setProperty.bind(editableElement.style);
				editableElement.style.setProperty = function(property, value, priority) {
					if (!showReplyPanel && (property === 'height' || property === 'min-height' || property === 'max-height')) {
						// When collapsed, force height-related properties to remain at 30px
						if (property === 'height' || property === 'min-height' || property === 'max-height') {
							return originalSetProperty(property, '30px', 'important');
						}
					}
					return originalSetProperty(property, value, priority);
				};
			}
		}

		// Set default font family and size like CKEditor4 does
		// Use defaultFontFamily prop if provided, otherwise default to 'Verdana'
		const fontFamily = defaultFontFamily !== undefined ? defaultFontFamily : 'Verdana';
		const fontSize = normalizeFontSizeToPt(defaultFontSize !== undefined ? defaultFontSize : 12);
		
		// Apply default font styles to the editor content area
		if (fontFamily || fontSize) {
			const editableElement = editor.ui.getEditableElement();
			const editorContainer = editableElement?.closest('.ckeditor5-component');
			
			if (editableElement && editorContainer) {
				if (fontFamily) {
					// Handle special case for Calibri like CKEditor4 does
					let processedFontFamily = fontFamily;
					if (fontFamily === "Calibri" || fontFamily === "calibri") {
						processedFontFamily = "Calibri,sans-serif,Arial";
					}
					
					// Set the CSS custom property for the editor container
					editorContainer.style.setProperty('--ck-font-face', `'${processedFontFamily}', sans-serif`);
					
					// Also set it directly on the editable element for immediate effect
					editableElement.style.fontFamily = `'${processedFontFamily}', sans-serif`;
				}
				if (fontSize) {
					editorContainer.style.setProperty('--ck-font-size', fontSize);
					editableElement.style.fontSize = fontSize;
				}

				// Set default text color if provided
				const defaultTxtColor = txtColor || 'black';
				editorContainer.style.setProperty('--ck-color-text', defaultTxtColor, 'important');
				editableElement.style.setProperty('color', defaultTxtColor, 'important');
			}
		}

		// Rename "Default" label in font size dropdown to show actual default size
		try {
			const fontSizePlugin = editor.plugins.get('FontSize');
			if (fontSizePlugin) {
				const defSizeNum = parseInt(normalizeFontSizeToPt(defaultFontSize !== undefined ? defaultFontSize : 12), 10) || 12;
				const customLabel = `Default (${defSizeNum}pt)`;
				// Override the dropdown button label when it shows "Default"
				const fontSizeCommand = editor.commands.get('fontSize');
				if (fontSizeCommand) {
					const originalValue = Object.getOwnPropertyDescriptor(
						Object.getPrototypeOf(fontSizeCommand), 'value'
					);
					// Patch the dropdown UI to show custom label
					const toolbarView = editor.ui.view.toolbar;
					for (const item of toolbarView.items) {
						if (item.buttonView && item.buttonView.label === 'Font Size') {
							// Found the font size dropdown
							const originalBind = item.buttonView.label;
							// Listen for label changes on the button
							const updateLabel = () => {
								setTimeout(() => {
									if (item.buttonView.label === 'Default') {
										item.buttonView.set('label', customLabel);
									}
								}, 0);
							};
							item.buttonView.on('change:label', updateLabel);
							// Also update dropdown list items
							if (item.panelView) {
								item.on('change:isOpen', () => {
									if (item.isOpen && item.panelView.element) {
										const buttons = item.panelView.element.querySelectorAll('.ck-list__item .ck-button');
										buttons.forEach(btn => {
											const label = btn.querySelector('.ck-button__label');
											if (label && label.textContent === 'Default') {
												label.textContent = customLabel;
											}
										});
									}
								});
							}
							// Set initial label if needed
							if (item.buttonView.label === 'Default') {
								item.buttonView.set('label', customLabel);
							}
							break;
						}
					}
				}
			}
		} catch (e) {
			console.warn('Could not customize font size default label:', e);
		}

		applyLegacyEditorColors(editor);

		// Set up word count if enabled
		if (showWordCount && editorWordCountRef.current) {
			const wordCount = editor.plugins.get('WordCount');
			editorWordCountRef.current.appendChild(wordCount.wordCountContainer);
		}

		// Set up text selection detection similar to CKEditor4
		if (onSelectText) {
			
			// Listen to selection change events
			editor.model.document.selection.on('change', () => {
				detectSelection(editor);
			});

			// Also listen to mouse events on the editable element for immediate feedback
			const editableElement = editor.ui.getEditableElement();
			if (editableElement) {

				// Use mouseup and keyup events like CKEditor4 does
				editableElement.addEventListener('mouseup', () => {
					// Small delay to let CKEditor5 update the selection
					setTimeout(() => detectSelection(editor), 10);
				});
				
				editableElement.addEventListener('keyup', () => {
					// Small delay to let CKEditor5 update the selection
					setTimeout(() => detectSelection(editor), 10);
				});
			} else {
			}
		} else {
		}

	
		const clipboardPipeline = editor.plugins.get('ClipboardPipeline');
		clipboardPipeline.on('inputTransformation', (evt, data) => {
			const fontFamily = defaultFontFamily !== undefined ? defaultFontFamily : 'Verdana';
			if (!fontFamily || fontFamily.trim() === '' || !data.content) {
				return;
			}

			if (data.dataTransfer) {
				if (data.dataTransfer.files && data.dataTransfer.files.length > 0) {
					return;
				}
				const types = data.dataTransfer.types || [];
				const hasFiles = types.includes('Files') || types.includes('application/x-moz-file');
				const hasImageData = types.some(type => type.startsWith('image/'));
				if (hasFiles || hasImageData) {
					return;
				}
			}

			let processedFontFamily = fontFamily;
			if (fontFamily === "Calibri" || fontFamily === "calibri") {
				processedFontFamily = "Calibri,sans-serif,Arial";
			}

			const pastedContent = editor.data.processor.toData(data.content);
			if (!pastedContent) {
				return;
			}

			let cleanedContent = cleanPastedContent(pastedContent);
			cleanedContent = `<span style="font-family: ${processedFontFamily}">${cleanedContent}</span>`;
			data.content = editor.data.processor.toView(cleanedContent);
		}, { priority: 'low' });

		// Set up Enter key handler for chat mode
		if (isChat) {
			editor.editing.view.document.on('enter', (evt, data) => {
				handleEnterKey(evt, data, editor);
			}, { priority: 'high' });
		}
		
		// Call the callback to expose the editor instance
		onEditorReady(editor);
		
		onReady(editor);
	};

	const handleChange = (event, editor) => {
		const data = editor.getData();
		
		// Mark this as an internal change to prevent the useEffect from resetting the editor
		isInternalChangeRef.current = true;
		lastExternalDataRef.current = data;
		
		// Always process image dimensions for email compatibility when editing signatures
		// or when specifically requested
		const processedData = (isSignatureEditor || isChat) ? 
			processImageDimensionsForEmail(data) : data;
		
		// Handle control prop logic like CKEditor 4 does
		if (control && processedData === (defaultContent || initialData || '')) {
			// If control is true and data matches default content, don't trigger onChange
			return;
		}
		
		// Create a CKEditor4-compatible event object for backward compatibility
		const ckEditor4CompatibleEvent = {
			editor: {
				getData: () => processedData,
				setData: (newData) => editor.setData(newData),
				name: name || id, // Use name prop or fall back to id
				document: {
					getBody: () => ({
						getText: () => {
							// Strip HTML tags to get plain text
							const tempDiv = document.createElement('div');
							tempDiv.innerHTML = processedData;
							return tempDiv.textContent || tempDiv.innerText || '';
						}
					})
				},
				// Add direct access to CKEditor 5 instance for advanced usage
				ck5Editor: editor
			}
		};
		
		// Call both onChange and onAnswerChange for backward compatibility
		onChange(ckEditor4CompatibleEvent);
		if (onAnswerChange) {
			onAnswerChange(ckEditor4CompatibleEvent);
		}
	};

	// Handle onKeyDown events (both casingconventions)
	const handleKeyDown = (keyEvt, editor) => {
		if (onKeyDown) {
			onKeyDown(keyEvt);
		}
		if (onKeydown) {
			onKeydown(keyEvt);
		}
	};

	// Handle Enter key for chat mode
	const handleEnterKey = useCallback((evt, data, editor) => {
		// Check if we're in chat mode (isChat can be a chat object or boolean)
		if (!isChat) {
			return; // Only handle Enter key in chat mode
		}
		
		// Check if Shift+Enter was pressed (soft enter)
		if (data.isSoft) {
			// Shift+Enter: Allow default behavior (new line)
			// Execute shiftEnter command to insert a line break
			editor.execute('shiftEnter');
			data.preventDefault();
			evt.stop();
			return;
		}
		
		// Enter without Shift: Send message
		// Prevent default behavior (new paragraph)
		data.preventDefault();
		evt.stop();
		
		// Create a CKEditor4-compatible event object for the onKeydown handler
		// This maintains backward compatibility with existing ChatInput code
		const ck4Event = {
			data: {
				keyCode: 13,
				domEvent: {
					$: evt.domEvent || {}
				}
			},
			cancel: () => {}
		};
		
		// Call the keydown handlers to trigger message sending
		if (onKeyDown) {
			onKeyDown(ck4Event);
		}
		if (onKeydown) {
			onKeydown(ck4Event);
		}
	}, [isChat, onKeyDown, onKeydown]);

	// Override onRecordEditorSize to prevent size recording when collapsed
	const handleRecordEditorSize = (height) => {
		// Only record size changes when the reply panel is expanded
		if (showReplyPanel && onRecordEditorSize) {
			onRecordEditorSize(height);
		}
		// When collapsed, don't report size changes to prevent external interference
	};

	// Handle focus events
	const handleFocus = (event, editor) => {
		if (showReplyPanel && height) {
			const restoreHeight = () => {
				const editableElement = editor.ui.getEditableElement();
				if (editableElement) {
					const targetHeight = userResizedHeightRef.current || height;
					editableElement.style.setProperty('height', targetHeight, 'important');
					editableElement.style.setProperty('min-height', height, 'important');
					editableElement.style.setProperty('max-height', '600px', 'important');
					editableElement.style.setProperty('resize', 'vertical', 'important');
				}
			};
			restoreHeight();
			setTimeout(restoreHeight, 0);
			setTimeout(restoreHeight, 50);
		}
		onFocus(event, editor);
	};

	// Handle blur events  
	const handleBlur = (event, editor) => {
		if (showReplyPanel && height) {
			const restoreHeight = () => {
				const editableElement = editor.ui.getEditableElement();
				if (editableElement) {
					const targetHeight = userResizedHeightRef.current || height;
					editableElement.style.setProperty('height', targetHeight, 'important');
					editableElement.style.setProperty('min-height', height, 'important');
					editableElement.style.setProperty('max-height', '600px', 'important');
					editableElement.style.setProperty('resize', 'vertical', 'important');
				}
			};
			restoreHeight();
			setTimeout(restoreHeight, 0);
			setTimeout(restoreHeight, 50);
			setTimeout(restoreHeight, 150);
		}
		onBlur(event, editor);
	};

	const handleAfterDestroy = () => {
		// Clean up global registry
		if (id && window.CKEditor5Instances) {
			delete window.CKEditor5Instances[id];
		}
		
		// Clean up CKEDITOR.instances compatibility
		if (id && window.CKEDITOR && window.CKEDITOR.instances) {
			delete window.CKEDITOR.instances[id];
		}
		
		if (showWordCount && editorWordCountRef.current) {
			Array.from(editorWordCountRef.current.children).forEach(child => child.remove());
		}
	};

	// Handle visibility props (hide, hidden, isReadOnly)
	if (hide || hidden) {
		return null;
	}

	if (!isLayoutReady) {
		return <div>Loading editor...</div>;
	}

	const containerClasses = [
		'editor-container',
		'editor-container_classic-editor',
		showWordCount ? 'editor-container_include-word-count' : '',
		finalClassName,
		!showReplyPanel ? 'collapsed-reply-panel' : '' // Add class when collapsed
	].filter(Boolean).join(' ');

	// Apply dark theme class like CKEditor4 does
	const themeClass = isDarkMode ? 'dark-theme' : 'light-theme';

	// Create container style that enforces collapsed height
	const containerStyle = {
		...style,
		...(showReplyPanel ? {} : {
			height: '30px',
			maxHeight: '30px',
			overflow: 'hidden',
			position: 'relative'
		})
	};

	return (
		<div 
			className={`ckeditor5-component ${finalClassName} ${themeClass}`} 
			style={containerStyle}
			data-qa-id={dataQaId}
		>
			{isLayoutReady && (
				<CKEditor
					editor={ClassicEditor}
					config={editorConfig}
					data={initialEditorData}
					onChange={handleChange}
					onReady={handleReady}
					onError={onError}
					onFocus={handleFocus}
					onBlur={handleBlur}
					onKeyDown={handleKeyDown}
					disabled={disabled || isReadOnly}
					onDestroy={handleAfterDestroy}
				/>
			)}
			{showWordCount && (
				<div 
					ref={editorWordCountRef}
					className="ck ck-word-count"
					style={{ 
						marginTop: '10px',
						fontSize: '12px',
						color: '#666',
						textAlign: 'right'
					}}
				/>
			)}
		</div>
	);
};

export default CKEditor5Component; 